/*
 * PDP eye (phones only: html[data-pdp-eye], set in layout/theme.liquid; markup and data: snippets/pdp-eye.liquid)
 *
 * On phones, the picked size and the picked frame get a "View" line with an eye; a tap on it - or on the picked
 * size or frame again - opens a preview of that choice right where the shopper is, the page not moving: the
 * design in a room at that size, in that frame (snippets/room-sizer.liquid), with that option's values
 * to try in it and the rest of the choice kept. "Choose this size / frame" makes the page's selection through the
 * page's own inputs, so everything else follows as for a tap; closing leaves the selection as it was. A rolled
 * canvas gets no eye and is not among the frames: there is nothing to see of it on a wall.
 *
 * The picker is re-rendered on every change: the eye follows (a mutation observer).
 */
(() => {
  const root = document.querySelector('[data-pdp-eye-root]');
  if (!root) return;

  const data = JSON.parse(root.querySelector('[data-pdp-eye-data]').textContent);
  const formId = data.formId;
  const POS = data.positions;
  const html = document.documentElement;
  const EYE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>';

  const isOn = () => html.hasAttribute('data-pdp-eye');

  /* ---- the eye on the picked size and frame ---- */

  const checked = (pos) => (pos ? document.querySelector(`input[type="radio"][form="${formId}"][data-option-position="${pos}"]:checked`) : null);
  const labelOf = (input) => (input ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);
  const isRolled = (value) => /^rolled/i.test(value || '');

  // a row the no-default A/B (no-default-pdp.js) still shows as unpicked has no pick to view
  const unpicked = (el) => !!el?.closest('[data-nd-unpicked]');

  const targets = () => {
    const list = [];
    const size = labelOf(checked(POS.size));
    if (size && size.getClientRects().length && !unpicked(size)) list.push(['size', size]);
    const frame = labelOf(checked(POS.frame));
    if (frame && frame.getClientRects().length && !isRolled(frame.dataset.optionValue) && !unpicked(frame)) list.push(['frame', frame]);
    return list;
  };

  const addEye = (kind, el) => {
    el.classList.add('pdp-eye-picked');
    if (el.querySelector(`[data-pdp-eye-open="${kind}"]`)) return;
    const eye = document.createElement('span');
    eye.className = kind === 'frame' ? 'pdp-eye-chip' : 'pdp-eye-line';
    eye.setAttribute('data-pdp-eye-open', kind);
    eye.setAttribute('role', 'button');
    eye.setAttribute('tabindex', '0');
    eye.setAttribute('aria-label', `See this ${kind} on a wall`);
    eye.innerHTML = `${EYE}<span>View</span>`;
    el.append(eye);
  };

  const decorate = () => {
    const list = isOn() ? targets() : [];
    // what is no longer picked loses its eye
    document.querySelectorAll('[data-pdp-eye-open]').forEach((eye) => {
      if (!list.some(([kind, el]) => kind === eye.getAttribute('data-pdp-eye-open') && el.contains(eye))) eye.remove();
    });
    document.querySelectorAll('.pdp-eye-picked').forEach((el) => {
      if (!list.some(([, picked]) => picked === el)) el.classList.remove('pdp-eye-picked');
    });
    list.forEach(([kind, el]) => addEye(kind, el));
  };
  let pending = 0;
  const schedule = () => {
    if (pending) return;
    pending = requestAnimationFrame(() => { pending = 0; decorate(); });
  };

  // the picker is re-rendered on every change (product-rerender); a tap changes the checked input at once
  const info = document.querySelector(`product-rerender[observe-form="${formId}"]`) || document.body;
  new MutationObserver(schedule).observe(info, { childList: true, subtree: true });
  document.addEventListener('change', (event) => {
    if (event.target?.getAttribute?.('form') === formId) decorate();
  });
  document.addEventListener('no-default:picks', schedule);
  schedule();

  /* ---- opening it ---- */

  document.addEventListener('click', (event) => {
    if (!isOn()) return;
    const eye = event.target.closest?.('[data-pdp-eye-open]');
    let kind = eye?.getAttribute('data-pdp-eye-open');
    if (!kind) {
      // the picked size or frame, tapped again
      const tapped = event.target.closest?.('label');
      const input = tapped?.htmlFor ? document.getElementById(tapped.htmlFor) : null;
      if (input?.checked && input.getAttribute('form') === formId && !unpicked(input)) {
        const pos = parseInt(input.dataset.optionPosition, 10);
        kind = pos === POS.size ? 'size' : pos && pos === POS.frame && !isRolled(tapped.dataset.optionValue) ? 'frame' : null;
      }
    }
    if (!kind) return;
    // before the label (the picked option, tapped again, does nothing else)
    event.preventDefault();
    event.stopPropagation();
    open(kind, eye || event.target.closest('label'));
  }, true);
  document.addEventListener('keydown', (event) => {
    const eye = event.target.closest?.('[data-pdp-eye-open]');
    if (!eye || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    open(eye.getAttribute('data-pdp-eye-open'), eye);
  });

  /* ---- the preview ---- */

  const optionIndex = { type: POS.type - 1, size: POS.size - 1, frame: POS.frame - 1 };

  const sizeText = (value) => {
    const [full, each] = String(value).split('|').map((s) => s.trim());
    const m = full.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!m) return escapeHtml(value);
    const w = +m[1], h = +m[2];
    const e = each && each.match(/(\d+(?:\.\d+)?)\s*x/i);
    const pieces = e ? Math.round(w / +e[1]) : 0;
    return `${pieces ? `${pieces}-piece ` : ''}<span class="pdp-eye-in">${w}″×${h}″</span>`
      + `<span class="pdp-eye-cm">${Math.round(w * 2.54)}×${Math.round(h * 2.54)} cm</span>`;
  };
  // splits ("68x34 | 34x34 ea.") after the single canvases, as in the page's size row
  const sortedSizes = () => data.sizes.filter((s) => !/ea\./i.test(s)).concat(data.sizes.filter((s) => /ea\./i.test(s)));

  const frameOf = (value) => data.frames.find((f) => f.v === value);
  // the frame pictures: the page's own swatches (the theme's swatch setting, not Shopify's swatches)
  const frameImage = (f) => {
    if (f.img === null || f.img === undefined) {
      const swatch = [...document.querySelectorAll('label[data-option-value][for]')]
        .find((l) => l.dataset.optionValue === f.v && document.getElementById(l.htmlFor)?.getAttribute('form') === formId);
      const bg = swatch ? getComputedStyle(swatch).getPropertyValue('--swatch-background').trim() : '';
      const url = bg.match(/url\((['"]?)(.*?)\1\)/);
      f.img = url ? url[2].replace(/width=\d+/, 'width=144') : '';
    }
    return f.img;
  };
  // the frame as words: "Shown as a stretched canvas", "Shown in a Walnut frame", "no Walnut frame at this size"
  const frameShown = (value) => {
    if (/^stretched/i.test(value)) return 'as a stretched canvas';
    if (/^rolled/i.test(value)) return 'as a rolled canvas';
    return `in a ${frameOf(value)?.name || value} frame`;
  };
  const frameShort = (value) => frameShown(value).replace(/^(as|in) an? /, '');

  const variantFor = (options) => data.variants.find((v) => v.o.every((o, i) => o === options[i]));
  const pageVariant = () => {
    try {
      return JSON.parse(document.querySelector(`variant-picker[form-id="${formId}"] script[data-variant]`).textContent);
    } catch (e) {
      return null;
    }
  };

  let sheet, rooms, base, state, row, opener, swapped;

  const build = async () => {
    if (sheet) return;
    document.body.append(root.querySelector('template[data-pdp-eye-sheet]').content.cloneNode(true));
    sheet = [...document.querySelectorAll('[data-pdp-eye-sheet-root]')].pop();
    // the room view's script, the first time
    if (!window.customElements.get('room-sizer')) {
      const script = document.createElement('script');
      script.src = root.dataset.roomSizerJs;
      document.head.append(script);
    }
    await window.customElements.whenDefined('room-sizer');
    rooms = sheet.querySelector('room-sizer');

    sheet.addEventListener('click', (event) => {
      if (event.target.closest('[data-pdp-eye-close]')) return close();
      const value = event.target.closest('[data-pdp-eye-value]');
      if (value && !value.disabled) return pick(value.getAttribute('data-pdp-eye-value'));
      if (event.target.closest('[data-pdp-eye-choose]')) return choose();
    });
    sheet.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
    // a drag down on the top of the sheet closes it
    let startY = null;
    sheet.querySelector('.pdp-eye-sheet__panel').addEventListener('pointerdown', (event) => {
      if (event.target.closest('.pdp-eye-sheet__handle, .pdp-eye-sheet__top') && !event.target.closest('button')) startY = event.clientY;
    });
    window.addEventListener('pointerup', (event) => {
      if (startY !== null && event.clientY - startY > 60) close();
      startY = null;
    });
  };

  const open = async (kind, from) => {
    await build();
    const variant = pageVariant();
    if (!variant?.options) return;
    opener = from;
    base = variant.options.slice();
    state = base.slice();
    swapped = null;
    row = kind;
    sheet.hidden = false;
    html.classList.add('pdp-eye-locked');
    requestAnimationFrame(() => {
      sheet.classList.add('is-open');
      sheet.querySelector('.pdp-eye-sheet__panel').focus({ preventScroll: true });
      paint();
    });
    track('open', kind);
  };

  const close = () => {
    if (!sheet || sheet.hidden) return;
    sheet.classList.remove('is-open');
    html.classList.remove('pdp-eye-locked');
    setTimeout(() => { if (!sheet.classList.contains('is-open')) sheet.hidden = true; }, 260);
    opener?.focus?.({ preventScroll: true });
  };

  // As on the page: a size can always be tried (pick() brings a frame it is made in along); a frame only when it
  // is made with the rest of the choice as it is - greyed out otherwise (54x72 has no frames)
  const possible = (i, value) => data.variants.some((v) => v.o[i] === value && v.o.every((o, k) => k === i
    || o === state[k] || (row === 'size' && k !== optionIndex.type)));

  // A value of the row. A size keeps the shopper's own frame (the page's, not one a bigger size brought along)
  // wherever it is made; a size not made in it (a split comes in stretched canvas only, 54x72 has no frames) is
  // shown in the closest variant with that size and the same type, stretched canvas first, and the label says so.
  const area = (value) => {
    const m = String(value || '').match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    return m ? m[1] * m[2] : 0;
  };
  const pick = (value) => {
    const i = optionIndex[row];
    if (!possible(i, value)) return;
    const wanted = row === 'size' && POS.frame ? Object.assign(state.slice(), { [optionIndex.frame]: base[optionIndex.frame] }) : state;
    const next = wanted.slice();
    next[i] = value;
    swapped = null;
    if (variantFor(next)) {
      state = next;
    } else {
      const here = area(wanted[optionIndex.size]);
      const score = (v) => v.o.filter((o, k) => o === wanted[k]).length * 1e6
        - Math.abs(area(v.o[optionIndex.size]) - here)
        + (/^stretched/i.test(v.o[optionIndex.frame] || '') ? 1 : 0);
      const near = data.variants.filter((v) => v.o[i] === value && (!POS.type || v.o[optionIndex.type] === wanted[optionIndex.type]))
        .sort((a, b) => score(b) - score(a))[0];
      if (!near) return;
      if (POS.frame && near.o[optionIndex.frame] !== wanted[optionIndex.frame]) swapped = wanted[optionIndex.frame];
      state = near.o.slice();
    }
    paint();
  };

  // A price as the page shows it (custom.js, GooliArtGlobalDiscount): once the shopper's code is applied, the
  // discounted price with the full one struck through; before that the code is only offered (the discount box),
  // and the price is the full one, as on the page
  const priceHtml = (v) => {
    const q = window.GooliArtGlobalDiscount?.quote?.(v.p);
    return q && q.applied ? `<s class="pdp-eye-was">${v.m}</s> ${q.discounted}` : v.m;
  };

  const paint = () => {
    const size = state[optionIndex.size];
    const frame = POS.frame ? state[optionIndex.frame] : '';
    requestAnimationFrame(() => {
      rooms.show(size, frame);
      if (row === 'size') rooms.preloadFor(frame);
    });

    // the label on the room: the frame in the size preview, the size in the frame preview (each where nothing
    // else names it) - and when a size brought its frame along, why
    sheet.querySelector('[data-pdp-eye-note]').innerHTML = swapped
      ? `Shown <b>${escapeHtml(frameShort(frame))}</b> · no ${escapeHtml(frameShort(swapped))} at this size`
      : row === 'size' ? (frame ? `Shown <b>${escapeHtml(frameShown(frame))}</b>` : '') : `Shown at <b>${sizeText(size)}</b>`;

    // that option's values
    const i = optionIndex[row];
    const list = row === 'size' ? sortedSizes() : data.frames.map((f) => f.v).filter((v) => !isRolled(v));
    const sw = sheet.querySelector('[data-pdp-eye-switch]');
    sw.className = `pdp-eye-switch pdp-eye-switch--${row}`;
    sw.innerHTML = list.map((value) => {
      const on = value === state[i];
      const attrs = `type="button" role="radio" aria-checked="${on}" data-pdp-eye-value="${escapeHtml(value)}" class="${on ? 'is-on' : ''}" ${possible(i, value) ? '' : 'disabled'}`;
      if (row === 'size') return `<button ${attrs}>${sizeText(value)}</button>`;
      const f = frameOf(value);
      const img = f ? frameImage(f) : '';
      return `<button ${attrs}><i ${img ? `style="background-image:url('${img}')"` : ''}></i><span>${escapeHtml(f?.name || value)}</span></button>`;
    }).join('');
    const on = sw.querySelector('.is-on');
    if (on && sw.scrollWidth > sw.clientWidth) sw.scrollLeft = on.offsetLeft - (sw.clientWidth - on.offsetWidth) / 2;

    // the pick itself is on its button in the row: not repeated here
    const btn = sheet.querySelector('[data-pdp-eye-choose]');
    const v = variantFor(state);
    btn.disabled = false;
    if (state.every((o, k) => o === base[k])) btn.textContent = 'Done';
    else if (!v) { btn.textContent = 'Not available with your other choices'; btn.disabled = true; }
    else if (!v.a) { btn.textContent = 'Sold out in this combination'; btn.disabled = true; }
    else btn.innerHTML = `Choose this ${row} · ${priceHtml(v)}`;
  };

  // the page's own inputs, so everything that follows a tap follows (prices, the gallery, the URL)
  const choose = () => {
    const changes = ['size', 'frame'].filter((k) => POS[k] && state[optionIndex[k]] !== base[optionIndex[k]]);
    close();
    changes.forEach((k) => {
      const value = state[optionIndex[k]];
      const input = [...document.querySelectorAll(`input[type="radio"][form="${formId}"][data-option-position="${POS[k]}"]`)]
        .find((el) => (el.dataset.optionValue || labelOf(el)?.dataset.optionValue) === value);
      if (input && !input.checked) input.click();
    });
    if (changes.length) track('choose', row);
  };

  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // the code applied (the discount box, the floating bar) with the preview open: its prices follow
  document.addEventListener('gooliart:discount-refresh', () => {
    if (sheet && !sheet.hidden) paint();
  });

  /* ---- measuring: Omniconvert goals (once per visitor per 30 days, as the other PDP goals) and the dataLayer ---- */

  const goal = (name, cookie) => {
    if (document.cookie.indexOf(`${cookie}=1`) !== -1 || typeof window._mktz !== 'object') return;
    window._mktz.push(['_Goal', name, 0]);
    const d = new Date();
    d.setTime(d.getTime() + 30 * 86400000);
    document.cookie = `${cookie}=1;expires=${d.toUTCString()};path=/;SameSite=Lax`;
  };
  const track = (what, kind) => {
    window.dataLayer?.push({ event: what === 'open' ? 'pdp_eye_open' : 'pdp_eye_choose', pdp_eye_option: kind });
    if (what === 'open') goal('used-preview-eye', 'gooliart_used_preview_eye');
    else goal('preview-eye-chose-option', 'gooliart_preview_eye_chose_option');
  };
})();
